import { RefuelErrorAction } from './types'
import { stripTechnicalCodes } from '../../../../services/messageText'

/** Minimum litres the station will dispense. Server code `2/6` is the same rule. */
export const MIN_LITERS = 2

/** Ceiling of the amount field — `Количество` is Decimal(15,2), 999.99 is the product cap. */
export const MAX_LITERS = 999.99

/** Metres within which a station counts as the one the driver is standing at. */
export const NEARBY_RADIUS = 400

/**
 * «Власний» — the wallet Mobile/Wallets hands every phone, under this one GUID
 * for everyone, on top of the driver's own. It can be refuelled from, but it
 * is an appendix: most drivers have exactly one wallet of their own plus this.
 */
export const SHARED_WALLET_GUID = 'e85339eb-b94d-11eb-90ed-005056b90e58'

/** Seconds between two attempts at a transient failure. */
export const RETRY_COOLDOWN = 30

/** Long enough for the driver to lift the nozzle, short enough to stand there. */
export const NOZZLE_COOLDOWN = 10

/** Total submissions allowed, the first one included. */
export const MAX_ATTEMPTS = 4

/** Litre shortcuts under the amount field. */
export const AMOUNT_TEMPLATES = [25, 50]

/*
    goodsCode -> label. The codes match the mobile app's fuel-types.ts.
    An unknown code falls back to the goodsName the station config returned.
 */
export const FUEL_LABELS: { [code: string]: string } = {
  '10001': 'ДП ЄВРО5',
  '10003': '92 ЄВРО5',
  '10004': '95 MUSTANG',
  '10005': '100 MUSTANG',
  '10006': 'ДП MUSTANG+',
  '10007': 'Газ',
  '10010': '95 ЄВРО5',
  '10999': 'Зарядка',
  '25535': 'AdBlue',
}

/**
 * Splits a label into the big tile caption and its qualifier:
 * `ДП MUSTANG+` -> `['ДП', 'MUSTANG+']`, `Газ` -> `['Газ', '']`.
 */
export const splitFuelLabel = (label: string): [string, string] => {
  const space = label.indexOf(' ')
  return space === -1
    ? [label, '']
    : [label.slice(0, space), label.slice(space + 1)]
}

export const getFuelLabel = (goodsCode: string, goodsName: string) =>
  FUEL_LABELS[goodsCode] || goodsName

/*
    Error map for ПродажаТоплива.

    The server's own message is always what the user reads — the pair only
    decides which button the failure screen offers, and how long it stays
    locked. Anything not listed here falls back to «Закрити», which is also the
    safe default: FillUpCar has no idempotency key, so an unrecognised failure
    must never offer a retry.
 */
export interface IRefuelErrorRule {
  action: RefuelErrorAction
  /** Seconds «Повторити» stays locked, when RETRY_COOLDOWN is the wrong pace. */
  cooldown?: number
}

export const REFUEL_ERRORS: { [pair: string]: IRefuelErrorRule } = {
  // Transient — safe to send the same order again after a pause.
  '1/1': { action: 'retry' }, // Сервіс тимчасово недоступний (загальний виняток)
  '2/1': { action: 'retry' }, // Не вдалось заблокувати замовлення по талону
  '1/18': { action: 'retry' }, // Замовлення в стані «ЗавершенСОшибкой»
  '2/20': { action: 'retry' }, // АЗК не відповідає (помилка виклику FuelOrder)
  '3/50': { action: 'retry' }, // Фронт не прийняв замовлення хоча АЗК доступна

  // «Не піднято пістолет» — the station refused the order before dispensing
  // anything, so no coupon exists and the same order may go again. The driver
  // fixes it at the pump in seconds; the transport-level pause would only make
  // the manager wait. This pair also covers a wrong fuel type, and the server's
  // own message is what tells the two apart.
  '2/48': { action: 'retry', cooldown: NOZZLE_COOLDOWN },

  // The manager has to change something before trying again.
  '2/6': { action: 'amount' }, // Кількість менша мінімальної — see MIN_LITERS
  '1/9': { action: 'station' }, // Немає АЗС за кодом

  // An order is already in flight. A second submission could issue a second
  // coupon and there is no key to protect against it, so never offer a retry.
  '1/17': { action: 'close' }, // Замовлення обробляється
}

/*
    Reading a failure.

    WOG Pay does not answer in the `{Status, Error, Result}` envelope the rest
    of Cabinet uses. A refusal comes back as

        {"status": 2, "error": 48, "message": {"ua": "…", "ru": "…", "en": "…"}}

    — lower-cased, `error` numeric rather than the envelope's `Error` text, and
    the text itself translated. Both shapes are read below, because the success
    path still arrives in the envelope and the shared fetcher signals a
    transport failure with its own `{error: true}`.
 */

const toNumber = (value: any): number | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

const firstNumber = (values: any[]): number | undefined => {
  for (const value of values) {
    const parsed = toNumber(value)
    if (parsed !== undefined) return parsed
  }
  return undefined
}

const toText = (value: any): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined

const firstText = (values: any[]): string | undefined => {
  for (const value of values) {
    const text = toText(value)
    if (text) return text
  }
  return undefined
}

/** `Status` in the plain envelope, `status` in a WOG Pay refusal. */
export const readStatus = (res: any): number | undefined =>
  firstNumber([res?.Status, res?.status, res?.Result?.status])

/**
 * The message the manager reads. Ukrainian, since the cabinet has no i18n —
 * `Error` is the envelope's own text and is kept as the last resort. The
 * backend writes its `(status, error)` pair into the text as well; that is
 * stripped here, so no screen has to remember to — the pair itself is read
 * by `resolveFailure`. Text made of nothing but codes reads as no text, and
 * the caller's own wording takes its place.
 */
export const readMessage = (res: any): string | undefined =>
  stripTechnicalCodes(
    firstText([
      res?.message?.ua,
      res?.message,
      res?.Result?.message?.ua,
      res?.Result?.message,
      res?.Error,
    ])
  )

export interface IRefuelFailureBody {
  status?: number
  error?: number
  message?: string
}

/**
 * The `(status, error)` pair and the text of a failed FillUpCar. Either code
 * may come back `undefined` — an unreadable pair routes the screen to
 * «Закрити», which is the safe default for a call with no idempotency key.
 */
export const resolveFailure = (res: any): IRefuelFailureBody => {
  const status = readStatus(res)
  // `Error` is text in the envelope, so it only counts when it parses numeric.
  const error = firstNumber([res?.error, res?.Error, res?.Result?.error])
  const failure = { status, error, message: readMessage(res) }

  if (
    process.env.REACT_APP_ENV !== 'production' &&
    (status === undefined || error === undefined)
  ) {
    console.warn('[WOG Pay] FillUpCar failed without a readable pair', res)
  }

  return failure
}

/** The rule for a pair, defaulting to the «Закрити»-only screen. */
export const getErrorRule = (
  status?: number,
  error?: number
): IRefuelErrorRule => {
  if (status === undefined || error === undefined) return { action: 'close' }
  return REFUEL_ERRORS[`${status}/${error}`] || { action: 'close' }
}

export const getRetryCooldown = (rule: IRefuelErrorRule) =>
  rule.cooldown === undefined ? RETRY_COOLDOWN : rule.cooldown
